import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  House,
  PlusCircle,
  ClockCounterClockwise,
  Users,
  UserCircle,
  SignOut,
  List,
  X,
  Crown,
  DownloadSimple,
  VideoCamera,
  Moon,
  Sun,
  TextAa,
  ShieldCheck,
  Question,
} from "@phosphor-icons/react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import { useFontScale } from "@/hooks/useFontScale";

function FontScaleControl() {
  const { scale, setFontScale, scales } = useFontScale();
  return (
    <div className="w-full px-3 py-2" data-testid="font-scale-control">
      <div className="flex items-center gap-2 text-sm font-medium text-gado-muted mb-1.5">
        <TextAa size={18} weight="fill" />
        <span>Tamanho da fonte</span>
      </div>
      <div className="grid grid-cols-3 gap-1 rounded-lg bg-gado-line/40 p-1">
        {scales.map((s) => (
          <button
            key={s.id}
            onClick={() => setFontScale(s.id)}
            data-testid={`font-scale-${s.id}`}
            aria-pressed={scale === s.id}
            aria-label={`Fonte ${s.label}`}
            className={`rounded-md py-1.5 font-semibold transition-all ${
              scale === s.id
                ? "bg-gado-green text-white shadow-sm"
                : "text-gado-muted hover:text-gado-ink hover:bg-white/70"
            } ${s.id === "normal" ? "text-xs" : s.id === "grande" ? "text-sm" : "text-base"}`}
          >
            {s.hint}
          </button>
        ))}
      </div>
    </div>
  );
}

const NAV = [
  { to: "/app", label: "Painel", icon: House, end: true, testid: "nav-dashboard" },
  { to: "/app/nova-pesagem", label: "Nova Pesagem", icon: PlusCircle, testid: "nav-nova-pesagem" },
  { to: "/app/analise-video", label: "Analisar Lote", icon: VideoCamera, testid: "nav-analise-video" },
  { to: "/app/historico", label: "Histórico", icon: ClockCounterClockwise, testid: "nav-historico" },
  { to: "/app/cadastros", label: "Cadastros", icon: Users, testid: "nav-cadastros" },
  { to: "/app/ajuda", label: "Ajuda", icon: Question, testid: "nav-ajuda" },
  { to: "/app/perfil", label: "Perfil", icon: UserCircle, testid: "nav-perfil" },
];

function ThemeToggle({ compact = false }) {
  const { effective, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={effective === "dark" ? "Ativar modo claro" : "Ativar modo escuro"}
      className={
        compact
          ? "p-2 rounded-md text-gado-muted hover:bg-gado-line/60 hover:text-gado-ink transition-colors"
          : "w-full inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gado-muted hover:bg-gado-line/50 hover:text-gado-ink transition-colors"
      }
    >
      {effective === "dark" ? (
        <>
          <Sun size={compact ? 20 : 18} weight="fill" className="text-gado-goldLight" />
          {!compact && <span>Tema claro</span>}
        </>
      ) : (
        <>
          <Moon size={compact ? 20 : 18} weight="fill" />
          {!compact && <span>Tema escuro</span>}
        </>
      )}
    </button>
  );
}

function SidebarContent({ user, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full">
      <Link
        to="/app"
        onClick={onNavigate}
        className="flex items-center gap-3 px-6 h-20 border-b border-gado-line"
      >
        {user?.logo ? (
          <img src={user.logo} alt="" className="h-10 w-10 rounded-md object-cover" />
        ) : (
          <img src="/logo-128.png" alt="GadoPro" className="h-11 w-11 rounded-md object-contain" />
        )}
        <div className="min-w-0 leading-tight">
          <div className="font-cabinet font-extrabold text-lg text-gado-ink truncate">
            {user?.fantasy_name || (
              <>Gado<span className="text-gado-gold">Pro</span></>
            )}
          </div>
          {!user?.fantasy_name && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-gado-muted truncate">
              Gestão Inteligente de Pesagens
            </div>
          )}
        </div>
      </Link>

      <nav className="flex-1 px-3 py-6 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-gado-green text-white"
                  : "text-gado-muted hover:bg-gado-line/50 hover:text-gado-ink"
              }`
            }
          >
            <Icon size={20} weight="regular" />
            {label}
          </NavLink>
        ))}
        {user?.role === "super_admin" && (
          <NavLink
            to="/admin"
            onClick={onNavigate}
            data-testid="nav-admin"
            className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-semibold text-gado-gold border border-gado-gold/30 bg-gado-gold/5 hover:bg-gado-gold/10 transition-colors mt-3"
          >
            <ShieldCheck size={20} weight="fill" />
            Área Administrativa
          </NavLink>
        )}
      </nav>

      <div className="border-t border-gado-line p-4">
        {user?.plan === "pro" && (
          <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-md bg-gado-gold/10 text-gado-gold text-sm font-semibold" data-testid="pro-badge">
            <Crown weight="fill" size={16} /> Plano Pro ativo
          </div>
        )}
        <div className="flex items-center gap-3 px-2 mb-3">
          <span className="h-9 w-9 rounded-full bg-gado-gold/20 text-gado-gold grid place-items-center font-semibold">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gado-ink truncate">{user?.name}</p>
            <p className="text-xs text-gado-muted truncate">{user?.email}</p>
          </div>
        </div>
        <div className="mb-1">
          <ThemeToggle />
          <FontScaleControl />
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-4 py-2 rounded-md text-sm text-gado-muted hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <SignOut size={18} /> Sair
        </button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("gp_install_dismissed") === "1"
  );

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const doInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    try {
      await installPrompt.userChoice;
    } catch { /* ignore */ }
    setInstallPrompt(null);
  };

  const dismissInstall = () => {
    localStorage.setItem("gp_install_dismissed", "1");
    setInstallDismissed(true);
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gado-cream flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-r border-gado-line flex-col fixed inset-y-0">
        <SidebarContent user={user} onLogout={onLogout} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-white border-r border-gado-line flex flex-col">
            <SidebarContent
              user={user}
              onLogout={onLogout}
              onNavigate={() => setOpen(false)}
            />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0 pl-safe pr-safe">
        {/* Mobile topbar */}
        <div className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gado-line pt-safe">
          <div className="h-14 flex items-center justify-between px-4">
            <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="p-1.5 -ml-1.5 rounded-md hover:bg-gado-line/60">
              {open ? <X size={22} /> : <List size={22} />}
            </button>
            <div className="flex items-center gap-2">
              <img src="/logo-128.png" alt="GadoPro" className="h-7 w-7 rounded-md object-contain" />
              <span className="font-cabinet font-extrabold text-lg text-gado-ink">
                Gado<span className="text-gado-gold">Pro</span>
              </span>
            </div>
            <ThemeToggle compact />
          </div>
        </div>

        <main className="p-5 md:p-8 lg:p-10 max-w-[1300px] mx-auto pb-safe-plus lg:pb-10">
          <Outlet />
        </main>

        {/* Mobile bottom nav — native app feel */}
        <nav
          className="lg:hidden fixed left-0 right-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-gado-line pb-safe"
          data-testid="bottom-nav"
        >
          <div className="grid grid-cols-7">
            {NAV.map(({ to, label, icon: Icon, end, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`bottom-${testid}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors ${
                    isActive ? "text-gado-green" : "text-gado-muted"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={20} weight={isActive ? "fill" : "regular"} />
                    <span className="leading-none truncate max-w-full">{label.split(" ")[0]}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* Install prompt banner */}
        {installPrompt && !installDismissed && (
          <div
            className="lg:hidden fixed left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] z-50 rounded-2xl bg-gado-green text-white shadow-xl px-4 py-3 flex items-center gap-3"
            data-testid="install-banner"
          >
            <span className="h-10 w-10 rounded-xl bg-gado-gold/20 grid place-items-center shrink-0">
              <DownloadSimple size={22} className="text-gado-goldLight" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Instalar o GadoPro</p>
              <p className="text-xs text-white/70">Acesso rápido do seu celular como um app.</p>
            </div>
            <button
              onClick={doInstall}
              data-testid="install-button"
              className="bg-gado-gold text-gado-ink px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-gado-goldLight"
            >
              Instalar
            </button>
            <button onClick={dismissInstall} className="text-white/60 hover:text-white p-1" aria-label="Fechar">
              <X size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
